import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Footer } from '../Components/Footer';
import HeroContainer from '../Components/ui/hero-container';
import ContactTestimonialCarousel from '../Components/contact/ContactTestimonialCarousel';
import FreeTrial from '../Components/about-page/free-trial';
import Carousel from '../Components/ui/carousel';
import Button from '../Components/ui/button/Button';
import HeaderAlt from '../Components/HeaderAlt';

const cards = [
  {
    title: 'Math',
    description:
      'Utilize our specialized math tools to tackle complex problems and improve your mathematical understanding.',
    icon: <img src="/images/icons/MathOperations.svg" alt="" />,
  },
  {
    title: 'History',
    description:
      'Dive deep into historical events and timelines with our comprehensive history resources.',
    icon: <img src="/images/icons/History.svg" alt="" />,
  },
  {
    title: 'Science',
    description:
      'Explore scientific concepts and conduct virtual experiments in our science spaces.',
    icon: <img src="/images/icons/Flask.svg" alt="" />,
  },
  {
    title: 'Spanish',
    description:
      'Enhance your language skills with our Spanish resources, including grammar tools, literature guides.',
    icon: <img src="/images/academic-courses/spain-flag.png" className="w-10" alt="" />,
  },
];

const feature = [
  {
    img: '/images/academic-courses/sol-img-6.png',
    title: 'Create Class Spaces',
    desc: 'Students can create personalized Class Spaces for each subject. Upload notes, slides, and materials to let AI tailor study plans, helping focus on relevant content and optimize learning.',
  },
  {
    img: '/images/academic-courses/sol-img-2.png',
    title: 'Generate Personalized Study Guides',
    desc: 'Create personalized study guides to simplify complex topics. Link uploaded materials for class-specific relevance and tailor guides to your learning style, understanding level, and personal interests.',
  },
  {
    img: '/images/academic-courses/sol-img-3.png',
    title: 'Practice Modules',
    desc: 'Use our customizable quiz modules to test yourself, get instant feedback, and generate study guides for areas needing improvement. Tailored to your level, these modules help you prepare for exams and strengthen understanding, with AI offering targeted support as you progress.',
  },
  {
    img: '/images/academic-courses/sol-img-1.png',
    title: 'Chat with Your AI Tutor',
    desc: 'Get 24/7 help from your AI tutor for personalized guidance. Ask questions, get explanations, and receive hints without giving away answers. Highlight sections for targeted feedback and tailored support to improve your understanding and performance.',
  },
  {
    img: '/images/academic-courses/sol-img-4.png',
    title: 'Tutor-Integrated',
    desc: "IAMAI's Academic Class Spaces can be purchased as a self-serve product or with our tutoring service. Our AI-trained tutors offer extra support, personalized assignments, and feedback to boost your learning experience.",
  },
  {
    img: '/images/academic-courses/sol-img-5.png',
    title: 'Parent Account Linking',
    desc: "Parents can link their accounts to monitor their child's study habits, performance, and interactions with AI and human tutors. Receive notifications or let your child manage their account independently.",
  },
];

const learningData = [
  {
    icon: '/images/icons/PersonArmsSpreadYellow.svg',
    title: 'Personalized Learning',
    desc: 'Tailored study guides and practice modules ensure that students receive the support they need.',
  },
  {
    icon: '/images/icons/Lego.svg',
    title: 'Organized Learning',
    desc: 'Students can keep all their class materials in one place, making it easier to study and stay on track.',
  },
  {
    icon: '/images/icons/UsersFour.svg',
    title: 'Enhanced Engagement',
    desc: "By incorporating students' interests into the study materials, IAMAI keeps students engaged.",
  },
  {
    icon: '/images/icons/Speedometer.svg',
    title: 'Improved Performance',
    desc: 'Regular practice and feedback help students improve their understanding & academic performance.',
  },
];

const AcademicCourses = () => {
  const navigate = useNavigate();
  return (
    <>
      <HeaderAlt />
      <div className="flex-container relative">
        <HeroContainer className="flex pb-[118px] pt-14">
          <div className="container flex flex-col gap-6 !px-6 md:flex-row md:justify-between md:gap-20">
            <div className="max-w-[570px] space-y-4 !pt-2 text-left md:space-y-6">
              <h1 className="fredokaOne w-full text-center text-[40px] font-normal  leading-tight tracking-[-0.8px] text-white md:text-left md:!text-[56px] md:tracking-[-1.12px]">
                AI-Powered Academic Class Spaces
              </h1>
              <p className="fredoka max-w-2xl text-center text-[18px] font-medium leading-[144.44%] tracking-[-0.18px] text-[#678285] md:text-left md:text-[22px] md:leading-[32px] md:tracking-[-0.22px]">
                Personalised Learning environment for all levels to help you stay organised and
                excel in your studies.
              </p>
              <div className="w-full pt-2 md:w-fit">
                <Button onClick={() => navigate('/waitlist')} label={'Start Free Trial'} />
              </div>
            </div>
            <div className="mt-6 flex-1 md:mt-0">
              <img
                src="/images/academic-courses/sol-hero-1.png"
                alt="hero-img"
                className="hidden md:block"
              />
              <img
                src="/images/academic-courses/sol1-hero-2.png"
                alt="hero-img"
                className="block md:hidden"
              />
            </div>
          </div>
        </HeroContainer>

        <div className="top-[470px] w-full items-center justify-center lg:absolute">
          <Carousel
            infinity={false}
            play={false}
            className="container !mx-auto !mt-[45px] grid  max-w-7xl grid-cols-1 gap-6 !px-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {cards.map((card, index) => (
              <div className="max-w-[250px] flex-none md:max-w-[25%]" key={index}>
                <SubjectCard
                  key={index}
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                />
              </div>
            ))}
          </Carousel>

          <div className="container !mx-auto !mt-14  flex !max-w-[606px] flex-col gap-7 !px-6">
            <h1 className="fredokaOne text-center text-[32px] font-normal  leading-normal tracking-[-0.64px] text-[#012E33] md:text-[40px] md:tracking-[-0.8px]">
              Revolutionize Learning with AI Powered Features
            </h1>
            <p className="text-center !font-[Fredoka] text-[18px] font-normal leading-normal tracking-[-0.18px] text-[#678285] md:text-[22px] md:tracking-[-0.22px]">
              AI-powered Class Spaces help your child stay organized, focused, and on track with
              personalized learning, setting them up for academic success.
            </p>
          </div>
          <div className="container !mt-14 flex flex-col gap-8">
            {feature.map((feature, i) => (
              <div
                key={i}
                className={`flex gap-4 p-6 md:px-10  md:py-6 ${i % 2 === 0 ? 'flex-col md:flex-row' : 'flex-col md:flex-row-reverse'} items-center justify-between rounded-[24px] bg-gradient-to-r from-white via-[#F7F7F7] to-white`}
              >
                <div className={`flex flex-1 justify-center`}>
                  <img
                    className="max-w-[300px] overflow-hidden rounded-full"
                    src={feature.img}
                    alt=""
                  />
                </div>
                <div className="mx-auto flex max-w-[437px] flex-col gap-6">
                  <h1 className="font-[Fredoka] text-[32px] font-medium leading-normal tracking-[-0.64px] text-[#012E33]">
                    {feature.title}
                  </h1>
                  <p className="font-[Fredoka] text-[22px] font-normal leading-[32px] tracking-[-0.22px] text-[#678285]">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="container !mt-[66px] space-y-10 !px-0">
            <h1 className="text-center text-[40px] font-semibold leading-normal tracking-[-0.8px] text-[#012E33]">
              Benefits of using IAMAI
            </h1>
            <div className="flex flex-col gap-4 md:flex-row">
              <Carousel play={false}>
                {learningData.map((data, i) => (
                  <div
                    key={i}
                    className="max-w-[250px] flex-none bg-[linear-gradient(136deg,_#FFF_8.91%,_#F7F7F7_50%)] md:max-w-[25%]"
                  >
                    <div
                      key={i}
                      className="flex flex-1 flex-col items-start gap-[24px] self-stretch rounded-[24px] bg-gradient-to-r from-white via-[#F7F7F7] to-white p-[37px_32px]"
                    >
                      <div className="flex justify-start">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#66CDCC]">
                          <img src={data.icon} alt="" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-left  font-[Fredoka] text-[32px] font-medium leading-normal tracking-[-0.64px] text-[#012E33]">
                          {data.title}
                        </h2>
                        <p className="text-left font-[Fredoka] text-[18px] font-normal leading-[26px] tracking-[-0.36px] text-[#678285]">
                          {data.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
          <div>
            <ContactTestimonialCarousel />
          </div>
          <div className="container !my-14">
            <FreeTrial />
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default AcademicCourses;

const SubjectCard = ({ icon, title, description }) => (
  <div className="flex h-full flex-1 flex-col items-start justify-between self-stretch rounded-[24px] bg-[linear-gradient(136deg,_#FFF_8.91%,_#F7F7F7_50%)] p-[37px_12px] md:p-[37px_32px]">
    <div className="mb-4 flex justify-start">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#66CDCC]">
        {icon}
      </div>
    </div>
    <div className="flex h-full flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-left font-[Fredoka] text-[32px] font-medium leading-normal tracking-[-0.64px] text-[#012E33]">
          {title}
        </h2>
        <p className="text-left font-[Fredoka] text-[18px] font-normal leading-[26px] tracking-[-0.36px] text-[#678285]">
          {description}
        </p>
      </div>
      <Link
        to="/waitlist"
        className="text-left font-[Fredoka] text-[18px] font-semibold leading-normal text-[#FC823E]"
      >
        Try it out →
      </Link>
    </div>
  </div>
);
